import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useBrand } from "@/lib/brand";
import { API } from "@/lib/api";
import { PRIVATE } from "@/components/PageviewTracker";

/**
 * WebChat: the live chat bubble on an agency's own Foundation site.
 *
 * It draws nothing itself. The bubble is one script served by the backend
 * (crm/web_chat_widget.py), the same script an agency pastes into a website
 * they already have, so there is one chat panel to keep right rather than
 * a React one here and a plain one there that drift apart.
 *
 * MOUNTED BESIDE THE PAGE COUNTER, at the root of both layouts, for the
 * reason PageviewTracker gives: anything rendered from a footer stops the
 * day a site design arrives with a footer of its own. It decides for itself
 * what is public, with the same PRIVATE list, and takes the bubble off the
 * CRM and the portals by hiding it rather than reloading the page.
 *
 * NOT ON FOUNDATION'S OWN SITE. "foundation" is the slug an unresolved
 * domain falls back to, and a bubble there would take questions in the
 * demo agency's name that nobody reads. The server refuses it anyway
 * (`web_chat.state_of` says no for any demo account); this keeps the
 * script from loading at all. Nor inside an iframe: an embedded listings
 * page already sits on a site with its own bubble, or none by choice.
 */
let injected = false;

function shouldShow(brand, pathname) {
  if (!brand.web_chat) return false;
  const slug = brand.tenant_slug;
  if (!slug || slug === "foundation") return false;
  if (PRIVATE.test(pathname)) return false;
  if (/(^|\/)embed(\/|$)/.test(pathname)) return false;
  try {
    if (window.self !== window.top) return false;
  } catch {
    return false;
  }
  return true;
}

export default function WebChat() {
  const brand = useBrand();
  const { pathname } = useLocation();

  useEffect(() => {
    if (typeof window === "undefined") return;
    const show = shouldShow(brand, pathname);
    // Read by the script when its config arrives, so a page that moved to
    // the CRM while the script was still loading does not get a bubble.
    window.__foundationChatHidden = !show;
    if (!show) {
      window.FoundationChat?.hide();
      return;
    }
    if (window.FoundationChat) {
      window.FoundationChat.show();
      return;
    }
    if (injected) return;
    injected = true;
    const s = document.createElement("script");
    s.src = `${API}/crm/public/chat/chat.js`;
    s.async = true;
    s.setAttribute("data-agency", brand.tenant_slug);
    // The classic design keeps its call and WhatsApp buttons in the bottom
    // right corner, so the bubble sits beside them rather than on top.
    const classicButtons = (brand.site_template || "classic") === "classic" && brand.business?.phone;
    s.setAttribute("data-offset", classicButtons ? "84" : "20");
    document.body.appendChild(s);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brand.web_chat, brand.tenant_slug, pathname]);

  return null;
}
